import { useEffect, useState } from 'react';
import { getSession } from '~/lib/auth';

/**
 * Today — at-a-glance dashboard. Pulls KPI tiles and today's visits
 * from /api/admin/today (admin-only, RLS-enforced).
 */

interface TodayBooking {
  id: string;
  name: string;
  slot_start: string;
  sheet_number: string | null;
  deposit_status: string | null;
  status: string;
}

interface TodayData {
  kpis: {
    bookings_today: number;
    week_revenue_cents: number;
    glp1_active: number;
  };
  todays_bookings: TodayBooking[];
}

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.valleyvitalitywellness.com';

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
function fmtCurrency(cents: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(cents / 100);
}

export function TodayPage() {
  const [data, setData] = useState<TodayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const session = getSession();
      if (!session) throw new Error('Not signed in');
      const res = await fetch(`${API_BASE}/api/admin/today`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const body = (await res.json()) as TodayData;
      setData(body);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ maxWidth: 1100, display: 'grid', gap: 24 }}>
      <header>
        <h1 style={{ fontSize: 32, color: 'var(--primary)', margin: 0, fontFamily: 'var(--font-display)' }}>Today</h1>
        <p style={{ color: 'var(--text-muted)', margin: '4px 0 0' }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </header>

      {error && (
        <div style={{ padding: 12, background: 'rgba(196, 69, 69, 0.08)', border: '1px solid rgba(196, 69, 69, 0.3)', borderRadius: 8, color: '#9a3535', fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        <Kpi label="Bookings today" value={loading ? '—' : String(data?.kpis.bookings_today ?? 0)} hint="Confirmed + pending" />
        <Kpi label="Revenue this week" value={loading ? '—' : fmtCurrency(data?.kpis.week_revenue_cents ?? 0)} hint="Last 7 days, payments table" />
        <Kpi label="GLP-1 active" value={loading ? '—' : String(data?.kpis.glp1_active ?? 0)} hint="Clients status=active" />
      </div>

      <section>
        <h2 style={{ fontSize: 18, color: 'var(--primary)', margin: '0 0 12px', fontFamily: 'var(--font-display)' }}>Today's bookings</h2>
        {loading ? (
          <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
        ) : !data?.todays_bookings.length ? (
          <div style={{ padding: 24, textAlign: 'center', background: 'var(--card)', border: '1px solid var(--line-soft)', borderRadius: 12 }}>
            <p style={{ color: 'var(--text-muted)', margin: 0 }}>No bookings scheduled for today.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {data.todays_bookings.map((b) => (
              <article
                key={b.id}
                style={{
                  display: 'grid', gridTemplateColumns: '90px 1fr auto', gap: 12,
                  padding: 14, background: 'var(--card)', border: '1px solid var(--line-soft)',
                  borderRadius: 10, alignItems: 'center',
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{fmtTime(b.slot_start)}</div>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--primary)' }}>{b.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {b.sheet_number ? `Sheet #${b.sheet_number}` : 'No sheet #'} · deposit {b.deposit_status ?? '—'}
                  </div>
                </div>
                <span
                  style={{
                    fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
                    padding: '4px 10px', borderRadius: 999,
                    background: 'rgba(95, 168, 162, 0.18)', color: 'var(--primary)',
                  }}
                >
                  {b.status}
                </span>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div style={{ padding: 20, background: 'var(--card)', border: '1px solid var(--line-soft)', borderRadius: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: 32, fontWeight: 500, color: 'var(--primary)', fontFamily: 'var(--font-display)', marginTop: 6 }}>
        {value}
      </div>
      {hint && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}
